import { Box, Text } from '@chakra-ui/react';
import { type JSX, useEffect, useRef } from 'react';

import { Notifications } from '@rta/rta-blueprint-library';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { useDesign } from '@hooks';

type BluePrintValidationPanelProps = {
	/** Notification to single out — a validation badge clicked in the 3D view sends its id */
	id?: string | null;
};

type ValidationFields = {
	location: string;
	panel: string;
	issue: string;
};

const ISSUE_MARKER = ' · Issue: ';
const LOCATION_PREFIX = 'Location: ';
const FIELD_SEPARATOR = ' · ';

// Splits the pre-joined validation description back into its labeled fields for a stacked display.
const parseValidationFields = (description: string): ValidationFields => {
	const markerIndex = description.indexOf(ISSUE_MARKER);
	if (markerIndex < 0) return { location: '', panel: '', issue: description };

	const whereSegment = description.slice(0, markerIndex);
	const issue = description.slice(markerIndex + ISSUE_MARKER.length);
	const where = whereSegment.startsWith(LOCATION_PREFIX) ? whereSegment.slice(LOCATION_PREFIX.length) : whereSegment;
	const [location, ...panelParts] = where.split(FIELD_SEPARATOR);

	return { location, panel: panelParts.join(FIELD_SEPARATOR), issue };
};

export const BluePrintValidationPanel = (props: BluePrintValidationPanelProps): JSX.Element => {
	const { id = null } = props;
	const [controller] = useDesign();
	const selectedRef = useRef<HTMLDivElement>(null);

	const kitchen = controller?.kitchen ?? null;
	const notifications = kitchen?.design.notifications ?? null;

	useEffect(() => {
		selectedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [id]);

	return <ContextPanelContent title={lang.contextPanels.title.blueprintValidation}>
		<Box padding="2" textAlign="left">
			{(notifications as unknown as Notifications)?.children.map((notification, index) => {
				const { location, panel, issue } = parseValidationFields(notification.description);
				const selected = Boolean(id) && notification.id === id;

				return (
					<Box
						key={notification.id ?? index}
						ref={selected ? selectedRef : undefined}
						padding="2"
						borderRadius="5"
						marginBottom="5"
						backgroundColor={selected ? '#fff4d6' : '#eee'}
						outline={selected ? '2px solid #e8921f' : undefined}
						boxShadow="0 0 5px 1px rgba(0,0,0,0.2)"
					>
						{location && <Text fontSize="14" marginBottom="1"><Text as="span" fontWeight="bold">{lang.blueprintStatus.label.location}:</Text> {location}</Text>}
						{panel && <Text fontSize="14" marginBottom="1"><Text as="span" fontWeight="bold">{lang.blueprintStatus.label.panel}:</Text> {panel}</Text>}
						{issue && <Text fontSize="14"><Text as="span" fontWeight="bold">{lang.blueprintStatus.label.issue}:</Text> {issue}</Text>}
					</Box>
				);
			})}
		</Box>
	</ContextPanelContent>;
};
